import React, { useMemo, memo } from "react";
import { addDays } from "date-fns";
import { Sun, Moon } from "lucide-react";
import { Card } from "@heroui/card";
import { IconSunset2 } from "@tabler/icons-react";
import useCalendarStore from "@/store/calendar-store";
import { Skeleton } from "@heroui/react";
import { useTranslations } from "../../../../../i18n";
import {
  dateToLocalTime,
  endOfDayTz,
  formatInTz,
  isPastTz,
  isSameDayTz,
  isTodayTz,
} from "@/utils/format-date-with-locale";
import { useStudioStore } from "@/store/studio-store";
import { TimeSlot } from "./timeslot";

interface DailyViewProps {
  events: any[];
  isAdmin?: boolean;
  onEventClick: (event: any) => void;
  isLoading?: boolean;
}

const getEventStart = (event: any) => event.startAt ?? event.startTime;
const getEventStartDate = (event: any) => {
  const startValue = getEventStart(event);
  if (!startValue) return null;

  const startDate = new Date(startValue);
  return Number.isNaN(startDate.getTime()) ? null : startDate;
};

// Memoized WeekSelector component
const WeekSelector = ({
  startOfTheWeek,
  currentDate,
  setCurrentDate,
  timezone,
  events,
}: {
  startOfTheWeek: Date;
  currentDate: Date;
  setCurrentDate: (date: Date) => void;
  timezone: string;
  events: any[];
}) => {
  const dayEventMeta = useMemo(() => {
    const meta: string[][] = [...Array(7)].map(() => []);

    if (!events || events.length === 0) {
      return meta;
    }

    events.forEach((event) => {
      const eventStart = getEventStartDate(event);
      if (!eventStart) return;

      const eventLocalTime = dateToLocalTime(eventStart, timezone);

      for (let index = 0; index < 7; index += 1) {
        const date = addDays(startOfTheWeek, index);

        if (!isSameDayTz(eventLocalTime, date, timezone)) continue;

        const color = event.color || event.class?.color;
        if (color && !meta[index].includes(color) && meta[index].length < 3) {
          meta[index].push(color);
        }
        break;
      }
    });

    return meta;
  }, [events, startOfTheWeek, timezone]);

  const days = useMemo(() => {
    return [...Array(7)].map((_, index) => {
      const date = addDays(startOfTheWeek, index);
      const isDateToday = isTodayTz(date);
      const isPastDay = isPastTz(endOfDayTz(date)) && !isDateToday;
      const isDateSameDay = isSameDayTz(date, currentDate);
      const dayMeta = dayEventMeta[index];

      return (
        <Card
          key={index}
          className="flex-1 text-center border-[2px] border-primary py-2 px-1 flex flex-col items-center justify-center"
          style={{
            borderColor: isDateSameDay ? "" : "transparent",
            opacity: isDateSameDay ? 1 : isPastDay ? 0.5 : 1,
          }}
          isPressable={!isPastDay}
          onPress={() => {
            if (!isPastDay) {
              setCurrentDate(date);
            }
          }}
        >
          <div
            className={`text-gray-700 text-md text-center flex items-center justify-center ${
              isDateToday ? "font-bold" : ""
            } `}
          >
            {formatInTz(date, "EEE", timezone)}
          </div>
          <div
            className={`text-gray-700 text-md flex items-center justify-center ${
              isDateToday ? "font-bold" : ""
            }`}
          >
            {formatInTz(date, "dd", timezone)}
          </div>
          <div className="mt-1 flex items-center justify-center gap-1">
            {dayMeta.map((color, i) => (
              <span
                key={i}
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </Card>
      );
    });
  }, [currentDate, dayEventMeta, setCurrentDate, startOfTheWeek, timezone]);

  return <div className="w-full flex gap-2 justify-between mb-8">{days}</div>;
};

WeekSelector.displayName = "WeekSelector";

// Memoized EventSection component
const EventSection = memo(
  ({
    title,
    Icon,
    events,
    onEventClick,
    t,
    timezone,
  }: {
    title: string;
    Icon: React.ComponentType<any>;
    events: any[];
    onEventClick: (event: any) => void;
    t: any; // Replace with your translation function
    timezone: string;
  }) => {
    if (!events || events.length === 0) return null;

    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 text-gray-600 border-b border-gray-200 pb-1 mb-2">
          <Icon className="w-6 h-6" />
          <span className="text-xl">{title}</span>
        </div>
        <div className="flex flex-col gap-2">
          {events.map((event) => (
            <TimeSlot
              key={event.id}
              event={event}
              onEventClick={onEventClick}
              t={t}
              timezone={timezone}
            />
          ))}
        </div>
      </div>
    );
  },
);

EventSection.displayName = "EventSection";

// Loading skeleton component
const LoadingSkeleton = () => (
  <>
    <div className="mb-6">
      <div className="flex items-center gap-2 text-gray-600 border-b border-gray-200 pb-1 mb-2">
        <IconSunset2 className="w-6 h-6" />
        <span className="text-xl">Morning</span>
      </div>
      <div className="flex flex-col gap-2">
        <Skeleton className="rounded-lg">
          <div className="h-24 rounded-lg bg-default-300" />
        </Skeleton>
      </div>
    </div>

    <div className="mb-6">
      <div className="flex items-center gap-2 text-gray-600 border-b border-gray-200 pb-1 mb-2">
        <Sun className="w-6 h-6" />
        <span className="text-xl">Afternoon</span>
      </div>
      <div className="flex flex-col gap-2">
        <Skeleton className="rounded-lg">
          <div className="h-24 rounded-lg bg-default-300" />
        </Skeleton>
        <Skeleton className="rounded-lg">
          <div className="h-24 rounded-lg bg-default-300" />
        </Skeleton>
      </div>
    </div>

    <div>
      <div className="flex items-center gap-2 text-gray-600 border-b border-gray-200 pb-1 mb-2">
        <Moon className="w-6 h-6" />
        <span className="text-xl">Evening</span>
      </div>
      <div className="flex flex-col gap-2">
        <Skeleton className="rounded-lg">
          <div className="h-24 rounded-lg bg-default-300" />
        </Skeleton>
      </div>
    </div>
  </>
);

const DailyView = ({
  events,
  isAdmin = false,
  onEventClick,
  isLoading,
}: DailyViewProps) => {
  const t = useTranslations("calendar");

  const organization = useStudioStore((state) => state.studio);
  const timezone = organization?.timezone || "Europe/Zurich";

  const { currentDate, setCurrentDate, startOfTheWeek } = useCalendarStore();

  // Memoized event filtering to avoid recalculation on every render
  const { morningEvents, afternoonEvents, eveningEvents } = useMemo(() => {
    if (!events || events.length === 0) {
      return { morningEvents: [], afternoonEvents: [], eveningEvents: [] };
    }

    const dailyEvents = events.filter((event) => {
      const eventStart = getEventStartDate(event);
      if (!eventStart) return false;

      return isSameDayTz(eventStart, currentDate, timezone);
    });

    const morning = dailyEvents.filter((event) => {
      const eventStart = getEventStartDate(event);
      if (!eventStart) return false;

      const hour = dateToLocalTime(eventStart, timezone).getHours();
      return hour >= 6 && hour < 12;
    });

    const afternoon = dailyEvents.filter((event) => {
      const eventStart = getEventStartDate(event);
      if (!eventStart) return false;

      const hour = dateToLocalTime(eventStart, timezone).getHours();
      return hour >= 12 && hour < 17;
    });

    const evening = dailyEvents.filter((event) => {
      const eventStart = getEventStartDate(event);
      if (!eventStart) return false;

      const hour = dateToLocalTime(eventStart, timezone).getHours();
      return hour >= 17;
    });

    return {
      morningEvents: morning,
      afternoonEvents: afternoon,
      eveningEvents: evening,
    };
  }, [events, currentDate, timezone]);

  // Display loading skeleton if data is loading
  if (isLoading) {
    return (
      <div className="p-2">
        <WeekSelector
          startOfTheWeek={startOfTheWeek}
          currentDate={currentDate}
          setCurrentDate={setCurrentDate}
          timezone={timezone}
          events={events}
        />
        <LoadingSkeleton />
      </div>
    );
  }

  return (
    <div className="p-2">
      <WeekSelector
        startOfTheWeek={startOfTheWeek}
        currentDate={currentDate}
        setCurrentDate={setCurrentDate}
        timezone={timezone}
        events={events}
      />

      <EventSection
        title={t("morning")}
        Icon={IconSunset2}
        events={morningEvents}
        onEventClick={onEventClick}
        t={t}
        timezone={timezone}
      />

      <EventSection
        title={t("afternoon")}
        Icon={Sun}
        events={afternoonEvents}
        onEventClick={onEventClick}
        t={t}
        timezone={timezone}
      />

      <EventSection
        title={t("evening")}
        Icon={Moon}
        events={eveningEvents}
        onEventClick={onEventClick}
        t={t}
        timezone={timezone}
      />

      {morningEvents.length === 0 &&
        afternoonEvents.length === 0 &&
        eveningEvents.length === 0 && (
          <div className="text-center text-gray-600">
            {t("noeventsthisday")}
          </div>
        )}
    </div>
  );
};

export default DailyView;
